import type { PageType } from "schemas/generated/analyticsRead";

import * as React from "react";

import twMerge from "@replo/design-system/utils/twMerge";
import { Combobox } from "@v1/components/designSystem/combobox/Combobox";
import { useAnalyticsQueryContext } from "@v1/features/analytics/contexts/AnalyticsQueryContext";

const PageTypeWithDescription: React.FC<{
  title: string;
  description: string;
  isSelected: boolean;
  className?: string;
}> = ({ title, description, isSelected, className }) => {
  return (
    <div
      className={twMerge(
        className,
        "flex w-full flex-col rounded-sm p-1.5",
        isSelected && "bg-selectable-selected",
        !isSelected && "hover:bg-selectable-hover",
      )}
    >
      <p className="text-xs font-semibold text-default">{title}</p>
      <p className="text-muted text-xs">{description}</p>
    </div>
  );
};

const PageTypeSelect: React.FC = () => {
  const { query, dispatchAnalyticsQuery } = useAnalyticsQueryContext();
  const [isOpen, setIsOpen] = React.useState(false);

  const handleOpenChange = () => {
    setIsOpen(!isOpen);
  };

  // Create a combined value to represent the current selection
  const currentValue = query.isFractional
    ? "fractional"
    : query.filters?.pageType;

  const options = [
    {
      displayValue: "Entry Pages",
      label: (
        <PageTypeWithDescription
          title="Entry Pages"
          description="Shows metrics for pages that began the session"
          isSelected={
            query.filters?.pageType === "entryPages" && !query.isFractional
          }
        />
      ),
      value: "entryPages",
      isDisabled: false,
    },
    {
      displayValue: "All Viewed Pages",
      label: (
        <PageTypeWithDescription
          title="All Viewed Pages"
          description="Data for any page visited within the session"
          isSelected={
            query.filters?.pageType === "allPages" && !query.isFractional
          }
        />
      ),
      value: "allPages",
      isDisabled: false,
    },
    {
      displayValue: "Fractional Attribution",
      label: (
        <PageTypeWithDescription
          title="Fractional Attribution"
          description="Distributes conversion credit across all pages in the session"
          isSelected={query.isFractional}
        />
      ),
      value: "fractional" as const,
      isDisabled: false,
    },
  ];

  const handleChange = (newValue: string) => {
    if (newValue === "fractional") {
      dispatchAnalyticsQuery({
        type: "updatePageTypeAndFractional",
        payload: {
          pageType: "entryPages",
          isFractional: true,
        },
      });
    } else {
      dispatchAnalyticsQuery({
        type: "updatePageTypeAndFractional",
        payload: {
          pageType: newValue as PageType,
          isFractional: false,
        },
      });
    }
  };

  return (
    <Combobox.Root
      options={options}
      open={isOpen}
      onOpenChange={handleOpenChange}
      value={currentValue ?? "allPages"}
      onChange={handleChange}
    >
      <Combobox.Trigger>
        <Combobox.SelectionButton
          size="base"
          title={
            options.find((option) => option.value === currentValue)
              ?.displayValue ?? ""
          }
          isPlaceholder={!currentValue}
        />
      </Combobox.Trigger>
      <Combobox.Popover side="bottom" align="end" layoutClassName="w-[300px]">
        <Combobox.Content size="lg" />
      </Combobox.Popover>
    </Combobox.Root>
  );
};

export default PageTypeSelect;
